import { useCallback, useRef } from 'react'

import { type PreferredLane } from 'preferred-lanes/domain/PreferredLane'
import { useSuggestedPreferredLanesContext } from 'preferred-lanes/ui/context/suggestedPreferredLanesContext'
import { useSuggestedPreferredLanes } from 'preferred-lanes/ui/hooks/useSuggestedPreferredLanes'

type SuggestPreferredLaneParams = PreferredLane & {
  radius: number | undefined
}

export function useTrackLaneSearch() {
  const { setPreferredLaneSuggestions } = useSuggestedPreferredLanesContext()
  const { trackLaneSearch: trackLane } = useSuggestedPreferredLanes()

  const debounceRef = useRef<number>()

  const suggestLaneAsPreferredLane = useCallback(
    async (params: SuggestPreferredLaneParams) => {
      const { origin, destination, equipmentTypes, radius } = params

      const laneSearch = {
        equipmentTypes,
        ...(origin && { origin: { ...origin, radius } }),
        ...(destination && { destination: { ...destination, radius } }),
      }

      const suggestions = await trackLane(laneSearch)
      setPreferredLaneSuggestions(suggestions)
    },
    [trackLane, setPreferredLaneSuggestions]
  )

  const trackLaneSearch = useCallback(
    (params: SuggestPreferredLaneParams) => {
      const { origin, destination } = params

      window.clearTimeout(debounceRef.current)

      if (origin && destination) {
        suggestLaneAsPreferredLane(params)
        return
      }

      if (origin || destination) {
        debounceRef.current = window.setTimeout(() => {
          window.clearTimeout(debounceRef.current)
          suggestLaneAsPreferredLane(params)
        }, 5000)
      }
    },
    [suggestLaneAsPreferredLane]
  )

  return { trackLaneSearch }
}
